<template>
    <div class="media-uploader">
        <label class="media-drop" :class="{ dragging: isDragging }" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="onDrop">
            <i class="fa-regular fa-cloud-arrow-up"></i>
            <span>{{ $t('dash.templates.management.media-upload-hint') }}</span>
            <input type="file" multiple :accept="acceptedFileTypesPerNetwork.default.join(',')" @change="onFileInput" @click="$event.target.value = null">
        </label>

        <p v-if="error" class="media-error">{{ error }}</p>

        <div class="media-list" v-if="items.length">
            <div class="media-item" v-for="item in items" :key="item.localId">
                <div class="media-thumb" :class="{ clickable: canPreview(item) }" @click="openViewer(item)">
                    <img v-if="item.previewUrl" :src="item.previewUrl" :alt="item.name">
                    <i v-else class="fa-solid" :class="thumbIcon(item)"></i>
                    <i v-if="item.isVideo" class="fa-solid fa-play media-play-badge"></i>
                </div>
                <span class="media-name">{{ item.name }}</span>
                <i v-if="item.status === 'uploading'" class="fa-solid fa-spinner fa-spin media-status"></i>
                <i v-else-if="item.status === 'error'" class="fa-solid fa-circle-exclamation media-status media-status-error" :title="item.error"></i>
                <i class="fa-solid fa-xmark media-remove" @click="removeItem(item)"></i>
            </div>
        </div>

        <div class="media-lightbox" v-if="viewingItem" @click.self="viewingItem = null">
            <i class="fa-solid fa-xmark media-lightbox-close" @click="viewingItem = null"></i>
            <img v-if="!viewingItem.isVideo" :src="viewingItem.url" :alt="viewingItem.name">
            <video v-else :src="viewingItem.url" controls autoplay></video>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { acceptedFileTypesPerNetwork } from '@/components/dash/postCreator/constante.js'
import postsMixin from '@/components/mixins/Posts.vue'

const { VUE_APP_API_HOST } = process.env

// Simple (non-multipart) upload path only, matching PostCreator.vue's own
// MULTIPART_THRESHOLD_BYTES - large files need the multipart flow (3 more S3 endpoints), not
// worth reproducing here for a Settings-page template uploader. Files above this are rejected
// with a clear message instead of silently failing partway through.
const MAX_FILE_BYTES = 8 * 1024 * 1024

let localIdCounter = 0

const normalizeFileNameForUpload = name => {
    const [base, ...rest] = name.split('.')
    const ext = rest.length ? `.${rest.pop()}` : ''
    const safeBase = base
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9._-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase()
    return (safeBase || 'file') + ext.toLowerCase()
}

export default {
    name: 'MediaTemplateUploader',

    mixins: [postsMixin],

    props: {
        value: {
            type: Array,
            default: () => []
        },
        // pre-existing {url, mimetype} files to seed the list with, for editing an existing
        // media template (already hosted - shown/removable immediately, no upload needed).
        // Parent should :key this component by the template's own id so switching which
        // existing template is being edited re-seeds instead of keeping the previous list.
        initialFiles: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            acceptedFileTypesPerNetwork,
            items: this.initialFiles.map(f => ({
                localId: `initial_${localIdCounter++}`,
                name: f.name || (f.url || '').split('/').pop() || f.mimetype,
                size: f.size,
                mimetype: f.mimetype,
                isVideo: (f.mimetype || '').startsWith('video/'),
                isPdf: f.mimetype === 'application/pdf',
                previewUrl: (f.mimetype || '').startsWith('image/') ? f.url : null,
                status: 'done',
                error: null,
                url: f.url
            })),
            isDragging: false,
            error: '',
            viewingItem: null
        }
    },

    mounted() {
        // so a parent relying on the v-model to know "is there content to save" sees the seeded
        // files immediately, not only after the user adds/removes something.
        if (this.items.length) this.emitValue()

        this.items
            .filter(i => i.isVideo && i.url)
            .forEach(i => this.captureVideoThumbnail(i, i.url))
    },

    methods: {
        onFileInput(event) {
            this.addFiles(event.target.files)
        },

        onDrop(event) {
            this.isDragging = false
            this.addFiles(event.dataTransfer.files)
        },

        // Same crossorigin="anonymous" + seek-then-draw approach as ThumbnailSelector.vue,
        // which already proves S3-hosted video sources allow canvas capture (not just blobs).
        // Best-effort: on failure (decode error, missing CORS headers) the item just keeps its
        // fallback icon + play badge, nothing else depends on this succeeding.
        async captureVideoThumbnail(item, src, revokeAfter) {
            const video = document.createElement('video')
            try {
                video.crossOrigin = 'anonymous'
                video.muted = true
                video.preload = 'auto'
                video.src = src

                await new Promise((resolve, reject) => {
                    video.addEventListener('loadedmetadata', resolve, { once: true })
                    video.addEventListener('error', reject, { once: true })
                })

                video.currentTime = Math.min(0.5, (video.duration || 1) / 2)

                await new Promise((resolve, reject) => {
                    video.addEventListener('seeked', resolve, { once: true })
                    video.addEventListener('error', reject, { once: true })
                })

                const width = 96
                const height = Math.round(width * ((video.videoHeight / video.videoWidth) || 1))
                const canvas = document.createElement('canvas')
                canvas.width = width
                canvas.height = height
                canvas.getContext('2d').drawImage(video, 0, 0, width, height)

                item.previewUrl = canvas.toDataURL('image/jpeg', 0.7)
            } catch (e) {
                // keep icon fallback
            } finally {
                if (revokeAfter) URL.revokeObjectURL(src)
            }
        },

        addFiles(fileList) {
            this.error = ''
            Array.from(fileList || []).forEach(file => {
                if (!acceptedFileTypesPerNetwork.default.includes(file.type)) {
                    this.error = this.$t('dash.templates.management.media-error-type', { name: file.name })
                    return
                }
                if (file.size > MAX_FILE_BYTES) {
                    this.error = this.$t('dash.templates.management.media-error-size', { name: file.name })
                    return
                }

                const isVideo = file.type.startsWith('video/')
                const item = {
                    localId: `local_${localIdCounter++}`,
                    name: file.name,
                    size: file.size,
                    mimetype: file.type,
                    isVideo,
                    isPdf: file.type === 'application/pdf',
                    previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
                    status: 'uploading',
                    error: null,
                    url: null
                }
                this.items.push(item)
                this.uploadItem(item, file)
                if (isVideo) this.captureVideoThumbnail(item, URL.createObjectURL(file), true)
            })
        },

        async uploadItem(item, file) {
            try {
                const res = await this.getUploadUrl(normalizeFileNameForUpload(file.name), file.type)
                const { uploadURL, urlFinal } = (res && res.body) || {}
                if (!uploadURL || !urlFinal) throw new Error('missing upload url')

                await axios.put(uploadURL, file, { headers: { 'Content-Type': file.type } })

                item.status = 'done'
                item.url = urlFinal
                this.emitValue()
            } catch (e) {
                item.status = 'error'
                item.error = this.$t('dash.templates.management.media-error-upload', { name: item.name })
            }
        },

        removeItem(item) {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
            this.items = this.items.filter(i => i.localId !== item.localId)
            if (this.viewingItem && this.viewingItem.localId === item.localId) this.viewingItem = null
            this.emitValue()
        },

        thumbIcon(item) {
            if (item.isPdf) return 'fa-file-pdf'
            if (item.isVideo) return 'fa-video'
            return 'fa-file'
        },

        canPreview(item) {
            return item.status === 'done' && !!item.url
        },

        // PDFs open in a new tab rather than an inline lightbox - reliable across browsers
        // without embedding a PDF renderer just for this Settings-page uploader.
        openViewer(item) {
            if (!this.canPreview(item)) return
            if (item.isPdf) {
                window.open(item.url, '_blank', 'noopener')
                return
            }
            this.viewingItem = item
        },

        emitValue() {
            const files = this.items
                .filter(i => i.status === 'done')
                .map(i => ({ url: i.url, mimetype: i.mimetype, name: i.name, size: i.size }))
            this.$emit('input', files)
        }
    }
}
</script>

<style lang="scss" scoped>
.media-uploader {
    .media-drop {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        border: 1.5px dashed #e3e6ee;
        border-radius: 8px;
        padding: 20px;
        cursor: pointer;
        color: #9ba3b7;
        font-size: 13px;
        text-align: center;

        i {
            font-size: 20px;
        }

        input {
            display: none;
        }

        &:hover,
        &.dragging {
            border-color: #6b1f4d;
            color: #6b1f4d;
        }
    }

    .media-error {
        color: #e14e4e;
        font-size: 12px;
        margin-top: 8px;
    }

    .media-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 12px;
    }

    .media-item {
        display: flex;
        align-items: center;
        gap: 10px;
        border: 1px solid #e3e6ee;
        border-radius: 6px;
        padding: 6px 10px;

        .media-thumb {
            position: relative;
            width: 32px;
            height: 32px;
            flex-shrink: 0;
            border-radius: 4px;
            overflow: hidden;
            background: #f5f6fa;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #9ba3b7;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            &.clickable {
                cursor: pointer;

                &:hover {
                    opacity: 0.8;
                }
            }

            .media-play-badge {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                color: #fff;
                text-shadow: 0 0 3px rgba(0, 0, 0, 0.6);
                pointer-events: none;
            }
        }

        .media-name {
            flex: 1;
            min-width: 0;
            font-size: 12px;
            color: #121826;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .media-status {
            color: #9ba3b7;
            font-size: 13px;

            &.media-status-error {
                color: #e14e4e;
            }
        }

        .media-remove {
            color: #9ba3b7;
            font-size: 13px;
            cursor: pointer;
            padding: 4px;

            &:hover {
                color: #e14e4e;
            }
        }
    }

    .media-lightbox {
        position: fixed;
        inset: 0;
        z-index: 2000;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;

        img,
        video {
            max-width: 90vw;
            max-height: 90vh;
            border-radius: 6px;
        }

        .media-lightbox-close {
            position: absolute;
            top: 20px;
            right: 24px;
            color: #fff;
            font-size: 22px;
            cursor: pointer;
        }
    }
}
</style>
